import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Save, Info, Coins } from "lucide-react";

// Independent rate settings for the Distribution Master Catalog. Completely
// separate from the main /rates collection — editing here MUST NOT affect
// the main 4-tier pricing chain.
export default function DistributionRates() {
  const { user: me } = useAuth();
  const canEdit = me?.role === "master_admin";
  const [r, setR] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/distribution-rates").then((x) => setR(x.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!r) return <div className="space-y-4"><div className="skeleton h-12 w-64" /><div className="skeleton h-96" /></div>;

  const setRoot = (k, v) => setR({ ...r, [k]: v });
  const setPurity = (k, v) => setR({ ...r, purity_factors: { ...r.purity_factors, [k]: parseFloat(v) || 0 } });
  const setWeightIndex = (k, v) => setR({ ...r, weight_index: { ...r.weight_index, [k]: parseFloat(v) || 0 } });
  const setTier = (tier, field, v) => setR({ ...r, tiers: { ...r.tiers, [tier]: { ...r.tiers[tier], [field]: parseFloat(v) || 0 } } });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        gold_rate_24kt: parseFloat(r.gold_rate_24kt) || 0,
        purity_factors: r.purity_factors,
        weight_index: r.weight_index,
        gst_rate: parseFloat(r.gst_rate) || 0,
        mrp_rounding: parseInt(r.mrp_rounding) || 100,
        // CS 21.32 -- persist the configurable min-labour weight threshold.
        min_labour_threshold_g: parseFloat(r.min_labour_threshold_g) || 1.25,
        tiers: r.tiers,
      };
      const { data } = await api.put("/distribution-rates", payload);
      setR(data);
      toast.success(`Distribution rates saved (v${data.version_number})`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="distribution-rates-page">
      <header className="flex items-end justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9A84C] mb-1">Distribution Module</p>
          <h1 className="font-serif text-4xl text-[#0D1B2A]">Distribution Rate Settings</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            Independent rate table used only by the Distribution Master Catalog. Changes here
            do NOT affect the main 4-tier pricing chain or franchise/distributor catalogues.
          </p>
        </div>
        {canEdit && (
          <Button onClick={save} disabled={saving} className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white" data-testid="dist-rates-save">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save changes"}
          </Button>
        )}
      </header>

      {!canEdit && (
        <div className="luxe-card p-4 bg-amber-50 border-amber-200 text-sm text-amber-800 flex gap-2 items-start" data-testid="dist-rates-readonly">
          <Info className="w-4 h-4 mt-0.5" /> Read-only — only Master Admin can edit Distribution rates.
        </div>
      )}

      {/* Gold + GST + Rounding */}
      <section className="luxe-card p-6">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C] mb-4 flex items-center gap-2">
          <Coins className="w-3.5 h-3.5" /> Base settings
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <NumField label="Gold rate 24kt (Rs/g)" value={r.gold_rate_24kt} onChange={(v) => setRoot("gold_rate_24kt", parseFloat(v) || 0)} ro={!canEdit} testid="dist-gold-24kt" />
          <NumField label="GST (%)" value={(r.gst_rate * 100).toFixed(2)} onChange={(v) => setRoot("gst_rate", (parseFloat(v) || 0) / 100)} ro={!canEdit} step="0.01" testid="dist-gst" />
          <NumField label="MRP rounding (nearest)" value={r.mrp_rounding} onChange={(v) => setRoot("mrp_rounding", parseInt(v) || 100)} ro={!canEdit} step="1" testid="dist-rounding" />
        </div>
      </section>

      {/* Pricing factors (gold rate) */}
      <section className="luxe-card p-6">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C] mb-1">Pricing factors</p>
        <p className="text-xs text-slate-500 mb-4">Applied to the 24kt gold rate to derive the gold RATE per gram at the selected purity. <span className="font-semibold">Not</span> used for weight conversion.</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <NumField label="24 kt" value={r.purity_factors?.["24KT"] ?? 1} onChange={(v) => setPurity("24KT", v)} ro={!canEdit} step="0.001" testid="dist-purity-24kt" />
          <NumField label="18 kt" value={r.purity_factors?.["18KT"] ?? 0} onChange={(v) => setPurity("18KT", v)} ro={!canEdit} step="0.001" testid="dist-purity-18kt" />
          <NumField label="14 kt" value={r.purity_factors?.["14KT"] ?? 0} onChange={(v) => setPurity("14KT", v)} ro={!canEdit} step="0.001" testid="dist-purity-14kt" />
          <NumField label="9 kt" value={r.purity_factors?.["9KT"] ?? 0} onChange={(v) => setPurity("9KT", v)} ro={!canEdit} step="0.001" testid="dist-purity-9kt" />
        </div>
        <p className="text-[11px] text-slate-400 mt-3">Defaults: 24kt 1.000 · 18kt 0.765 · 14kt 0.595 · 9kt 0.390</p>
      </section>

      {/* Weight conversion index */}
      <section className="luxe-card p-6 border-l-4 border-[#b8893b]/40">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#b8893b] mb-1">Weight conversion table</p>
        <p className="text-xs text-slate-500 mb-4">Single anchor table (18kt = 1.000) used to convert a product's stored Net Wt into the equivalent gram-weight at the toggled purity. Formula: <span className="font-mono bg-slate-50 px-1.5 py-0.5 rounded">converted = base × (target / base_index)</span>. Round-trip safe.</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <NumField label="24 kt index" value={r.weight_index?.["24KT"] ?? 1.205} onChange={(v) => setWeightIndex("24KT", v)} ro={!canEdit} step="0.001" testid="dist-weight-24kt" />
          <NumField label="18 kt index (anchor)" value={r.weight_index?.["18KT"] ?? 1} onChange={(v) => setWeightIndex("18KT", v)} ro={!canEdit} step="0.001" testid="dist-weight-18kt" />
          <NumField label="14 kt index" value={r.weight_index?.["14KT"] ?? 0.830} onChange={(v) => setWeightIndex("14KT", v)} ro={!canEdit} step="0.001" testid="dist-weight-14kt" />
          <NumField label="9 kt index" value={r.weight_index?.["9KT"] ?? 0.750} onChange={(v) => setWeightIndex("9KT", v)} ro={!canEdit} step="0.001" testid="dist-weight-9kt" />
        </div>
        <p className="text-[11px] text-slate-400 mt-3">Defaults: 18kt 1.000 (anchor) · 14kt 0.830 · 9kt 0.750 · 24kt 1.205 (info). Example: a product stored at 18kt 10g → toggled to 9kt shows {(10 * 0.75).toFixed(2)} g.</p>
      </section>

      {/* Tier rates */}
      <section className="luxe-card p-6">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#C9A84C] mb-4">Tier rates</p>
        <div className="mb-4 p-3 rounded-md border border-[#0D1B2A]/10 bg-[#FAF8F3]" data-testid="dist-min-labour-threshold-group">
          <NumField
            label="Minimum-labour weight threshold (grams)"
            value={r.min_labour_threshold_g ?? 1.25}
            onChange={(v) => setRoot("min_labour_threshold_g", parseFloat(v) || 0)}
            ro={!canEdit} step="0.01"
            testid="dist-min-labour-threshold"
          />
          <p className="text-[11px] text-slate-500 mt-2">
            Pieces with gold weight below this value use the tier&apos;s flat &ldquo;Minimum Labour&rdquo;; at or above, per-gram × weight. Boundary (exactly this value) uses per-gram.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {["distributor", "retailer", "customer"].map((tier) => {
            const thr = Number(r.min_labour_threshold_g ?? 1.25);
            const thrLabel = Number.isFinite(thr) ? thr.toFixed(2).replace(/\.?0+$/, "") : "1.25";
            return (
              <div key={tier} className="space-y-3 p-4 rounded-md border border-[#0D1B2A]/10 bg-[#FAF8F3]" data-testid={`dist-tier-${tier}`}>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#0D1B2A] font-semibold">
                  {tier === "customer" ? "Customer MRP" : tier.charAt(0).toUpperCase() + tier.slice(1)}
                </p>
                <NumField label={`Labour (Rs/g, ≥ ${thrLabel}g)`} value={r.tiers?.[tier]?.labour ?? 0} onChange={(v) => setTier(tier, "labour", v)} ro={!canEdit} step="1" testid={`dist-${tier}-labour`} />
                <NumField label={`Minimum Labour (< ${thrLabel}g)`} value={r.tiers?.[tier]?.min_labour ?? 0} onChange={(v) => setTier(tier, "min_labour", v)} ro={!canEdit} step="1" testid={`dist-${tier}-min-labour`} />
                <NumField label="Diamond (Rs/ct)" value={r.tiers?.[tier]?.diamond ?? 0} onChange={(v) => setTier(tier, "diamond", v)} ro={!canEdit} step="1" testid={`dist-${tier}-diamond`} />
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function NumField({ label, value, onChange, ro, step = "0.01", testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">{label}</label>
      <Input
        type="number"
        step={step}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        readOnly={ro}
        disabled={ro}
        data-testid={testid}
        className={ro ? "opacity-60 cursor-not-allowed" : ""}
      />
    </div>
  );
}
